import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { RadarChart } from "../components/charts/RadarChart";
import { HeatMap } from "../components/charts/HeatMap";
import { API, CATEGORY_COLORS, formatDate } from "../lib/utils";
import { toast } from "sonner";
import {
  Users,
  Copy,
  Check,
  RefreshCw,
  Download,
  Eye,
  MoreVertical,
  BarChart3,
  Grid3X3,
  UserCheck,
  Clock,
  ArrowLeft,
  Share2,
  Loader2,
} from "lucide-react";

export default function FacilitatorDashboard() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, aggregateRes, itemsRes] = await Promise.all([
        axios.get(`${API}/sessions/${sessionId}/dashboard`),
        axios.get(`${API}/sessions/${sessionId}/aggregate`),
        axios.get(`${API}/assessment-items`),
      ]);
      
      setDashboard(dashboardRes.data);
      setAggregate(aggregateRes.data);
      setCategories(itemsRes.data.categories);
      setItems(itemsRes.data.items);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchData();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      setRefreshing(true);
      fetchData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const copyInviteCode = () => {
    if (dashboard?.session?.invite_code) {
      navigator.clipboard.writeText(dashboard.session.invite_code);
      setCopied(true);
      toast.success("Invite code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyInviteLink = () => {
    const link = `${window.location.origin}?code=${dashboard?.session?.invite_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied!");
  };

  const viewMemberDetail = async (member) => {
    setSelectedMember(member);
    try {
      const response = await axios.get(
        `${API}/sessions/${sessionId}/members/${member.id}/detail`
      );
      setMemberDetail(response.data);
    } catch (error) {
      toast.error("Failed to load member details");
    }
  };

  const handleExportTeamReport = () => {
    if (!aggregate || !dashboard) return;
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Team Report - ${dashboard.session.name}</title>
          <style>
            body { font-family: Inter, sans-serif; padding: 2rem; max-width: 900px; margin: 0 auto; }
            h1 { font-size: 1.5rem; margin-bottom: 0.5rem; }
            h2 { font-size: 1.25rem; margin-top: 1.5rem; border-bottom: 2px solid #2C3E50; padding-bottom: 0.5rem; }
            .stat { display: inline-block; padding: 1rem; background: #f5f5f5; margin: 0.5rem; border-radius: 8px; text-align: center; }
            .stat-value { font-size: 2rem; font-weight: bold; color: #2C3E50; }
            .stat-label { font-size: 0.875rem; color: #666; }
            table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
            th, td { padding: 0.75rem; text-align: left; border-bottom: 1px solid #eee; }
            th { background: #f5f5f5; font-weight: 600; }
            .score { font-weight: bold; font-size: 1.25rem; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Team Assessment Report</h1>
          <p><strong>Session:</strong> ${dashboard.session.name}</p>
          <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          
          <div style="margin: 2rem 0;">
            <div class="stat">
              <div class="stat-value">${aggregate.participant_count}</div>
              <div class="stat-label">Participants</div>
            </div>
            <div class="stat">
              <div class="stat-value">${aggregate.submitted_count}</div>
              <div class="stat-label">Submitted</div>
            </div>
            <div class="stat">
              <div class="stat-value">${aggregate.overall_average.toFixed(1)}</div>
              <div class="stat-label">Overall Average</div>
            </div>
          </div>
          
          <h2>Category Scores</h2>
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Average Score</th>
                <th>Responses</th>
              </tr>
            </thead>
            <tbody>
              ${aggregate.category_averages
                .map(
                  (cat) => `
                <tr>
                  <td>${cat.category_name}</td>
                  <td class="score">${cat.average.toFixed(1)}</td>
                  <td>${cat.response_count}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <h2>Participants</h2>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              ${dashboard.members
                .map(
                  (m) => `
                <tr>
                  <td>${m.display_name}</td>
                  <td>${m.submitted_at ? "Submitted" : "In Progress"}</td>
                  <td>${m.progress}%</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">Session not found</p>
            <Button onClick={() => navigate("/")}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { session, members, stats } = dashboard;

  return (
    <div className="min-h-screen bg-background pb-8" data-testid="facilitator-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{session.name}</h1>
                <p className="text-sm text-muted-foreground">Facilitator Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportTeamReport}
                data-testid="btn-export-team"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Invite Code Card */}
        <Card className="mb-6 border-primary/20 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <p className="text-sm text-muted-foreground mb-1">Invite Code</p>
                <p className="text-3xl font-bold tracking-wider">{session.invite_code}</p>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyInviteCode} data-testid="btn-copy-code">
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  Copy Code
                </Button>
                <Button variant="outline" onClick={copyInviteLink} data-testid="btn-copy-link">
                  <Share2 className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.total_participants}</p>
                  <p className="text-sm text-muted-foreground">Participants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <UserCheck className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.submitted}</p>
                  <p className="text-sm text-muted-foreground">Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{stats.in_progress}</p>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="participants" className="space-y-6">
          <TabsList>
            <TabsTrigger value="participants" data-testid="tab-participants">
              <Users className="h-4 w-4 mr-2" />
              Participants
            </TabsTrigger>
            <TabsTrigger value="aggregate" data-testid="tab-aggregate">
              <BarChart3 className="h-4 w-4 mr-2" />
              Team Results
            </TabsTrigger>
            <TabsTrigger value="heatmap" data-testid="tab-heatmap">
              <Grid3X3 className="h-4 w-4 mr-2" />
              Heat Map
            </TabsTrigger>
          </TabsList>

          {/* Participants Tab */}
          <TabsContent value="participants">
            <Card>
              <CardHeader>
                <CardTitle>Participant Status</CardTitle>
                <CardDescription>
                  Real-time view of participant progress
                </CardDescription>
              </CardHeader>
              <CardContent>
                {members.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No participants have joined yet</p>
                    <p className="text-sm mt-2">Share the invite code to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                        data-testid={`participant-${member.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-primary">
                            {member.display_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{member.display_name}</p>
                            <p className="text-sm text-muted-foreground">
                              Joined {formatDate(member.joined_at)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right hidden sm:block">
                            <Badge
                              variant={member.submitted_at ? "default" : "secondary"}
                            >
                              {member.submitted_at ? "Submitted" : "In Progress"}
                            </Badge>
                          </div>
                          
                          <div className="w-24">
                            <Progress value={member.progress} className="h-2" />
                            <p className="text-xs text-muted-foreground text-right mt-1">
                              {member.progress}%
                            </p>
                          </div>
                          
                          {member.submitted_at && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => viewMemberDetail(member)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aggregate Results Tab */}
          <TabsContent value="aggregate">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Team Radar</CardTitle>
                  <CardDescription>
                    Average scores across all categories
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {aggregate?.submitted_count > 0 ? (
                    <div className="radar-container">
                      <RadarChart
                        data={aggregate.category_averages}
                        className="w-full h-full"
                      />
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      <p>Waiting for submissions...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Category Breakdown</CardTitle>
                  <CardDescription>
                    Detailed scores by category
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {aggregate?.category_averages.map((cat) => (
                      <div key={cat.category_id}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: CATEGORY_COLORS[cat.category_id] }}
                            />
                            <span className="text-sm font-medium">{cat.category_name}</span>
                          </div>
                          <span className="font-bold">{cat.average.toFixed(1)}</span>
                        </div>
                        <Progress
                          value={cat.average * 10}
                          className="h-2"
                        />
                      </div>
                    ))}
                    
                    <Separator className="my-4" />
                    
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Overall Average</span>
                      <span className="text-2xl font-bold">
                        {aggregate?.overall_average.toFixed(1) || "—"}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Heat Map Tab */}
          <TabsContent value="heatmap">
            <Card>
              <CardHeader>
                <CardTitle>Team Heat Map</CardTitle>
                <CardDescription>
                  Visual representation of average scores per item
                </CardDescription>
              </CardHeader>
              <CardContent>
                {aggregate?.submitted_count > 0 ? (
                  <HeatMap
                    data={aggregate.item_averages}
                    categories={categories}
                    items={items}
                  />
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    <p>Waiting for submissions to generate heat map...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Member Detail Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedMember?.display_name}'s Assessment</DialogTitle>
            <DialogDescription>
              Submitted {formatDate(selectedMember?.submitted_at)}
            </DialogDescription>
          </DialogHeader>
          
          {memberDetail && (
            <div className="space-y-6">
              {Object.entries(memberDetail.categories).map(([catId, catData]) => (
                <div key={catId}>
                  <div
                    className="flex items-center gap-2 mb-3 pb-2 border-b"
                    style={{ borderColor: CATEGORY_COLORS[catId] }}
                  >
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: CATEGORY_COLORS[catId] }}
                    />
                    <h3 className="font-semibold">{catData.category.name}</h3>
                    <Badge variant="outline" className="ml-auto">
                      Avg: {catData.average.toFixed(1)}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3">
                    {catData.items.map(({ item, response }) => (
                      <div key={item.id} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium">{item.name}</span>
                          <span className="font-bold text-lg">
                            {response?.rating ?? "N/A"}
                          </span>
                        </div>
                        
                        {response?.q1_text && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">Why:</p>
                            <p className="text-sm">{response.q1_text}</p>
                          </div>
                        )}
                        
                        {response?.q2_text && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">Commitment:</p>
                            <p className="text-sm">{response.q2_text}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
